/**
 * Правила мотивации — тиры % мотивации по % выполнения плана.
 * Хранятся в БД (motivation_rules) с историей по периодам (valid_from..valid_to).
 * Редактируется без правки кода / деплоя.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/prototype/Icon";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import { useAuth } from "@/auth/AuthContext";
import { fetchMotivationRules, type MotivationRule, type PayrollSnapshotTier, type PayrollSnapshotAdvanceTier } from "@/api/finance";
import { api } from "@/api/client";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
}

interface RulePatchBody {
  valid_from?: string; valid_to?: string | null;
  tiers?: PayrollSnapshotTier[];
  advance_tiers?: PayrollSnapshotAdvanceTier[];
  comment?: string | null;
}
const patchRule = (id: number, body: RulePatchBody) =>
  api(`/api/finance/payroll/motivation-rules/${id}`, { method: "PATCH", body: JSON.stringify(body) });
const createRule = (body: { role: string; valid_from: string; valid_to?: string | null; tiers: PayrollSnapshotTier[]; advance_tiers?: PayrollSnapshotAdvanceTier[]; comment?: string | null }) =>
  api(`/api/finance/payroll/motivation-rules`, { method: "POST", body: JSON.stringify(body) });
const deleteRule = (id: number) =>
  api(`/api/finance/payroll/motivation-rules/${id}`, { method: "DELETE" });
const activateRule = (id: number) =>
  api(`/api/finance/payroll/motivation-rules/${id}/activate`, { method: "POST" });

export function FinanceMotivationRulesPage() {
  const { user } = useAuth();
  const canEdit = !!user?.is_superuser
    || user?.role === "owner" || user?.role === "admin" || user?.role === "accountant";
  const [role, setRole] = useState("sales");
  const [newOpen, setNewOpen] = useState(false);
  const q = useQuery({
    queryKey: ["motivation-rules", role],
    queryFn: () => fetchMotivationRules(role),
    staleTime: 60_000,
  });
  return (
    <FinanceLayout>
      <div className="filter-bar" style={{ marginBottom: "var(--gap)", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Правила мотивации</div>
          <div style={{ fontSize: 12, color: "var(--mute)" }}>
            Тиры % мотивации, определяемые от % выполнения плана (договоры команды / план месяца). История по периодам (valid_from..valid_to).
            Считаются на бэкенде для compute_payroll → ведомости.
          </div>
        </div>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--mute)" }}>Роль:</span>
        <select className="input-sm" value={role} onChange={e => setRole(e.target.value)}>
          <option value="sales">Отдел продаж</option>
          <option value="production">Производство</option>
          <option value="field">Выездной</option>
          <option value="admin">Администрация</option>
        </select>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setNewOpen(true)}>
            <Icon name="plus" size={12}/> Новое правило
          </button>
        )}
      </div>

      <div className="card">
        {q.isLoading ? <div className="muted" style={{ padding: 20 }}>Загрузка…</div>
          : q.error ? <div className="alert alert-red"><div className="alert-body">{(q.error as Error).message}</div></div>
          : (q.data?.length ?? 0) === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "var(--mute)" }}>
              Правил для роли «{role}» нет. {canEdit && "Нажмите «Новое правило»."}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 14 }}>
              {(q.data ?? []).map(rule => (
                <RuleCard key={rule.id} rule={rule} canEdit={canEdit}/>
              ))}
            </div>
          )}
      </div>

      {newOpen && <NewRuleModal role={role} onClose={() => setNewOpen(false)} onCreated={() => { setNewOpen(false); q.refetch(); }}/>}
    </FinanceLayout>
  );
}

function RuleCard({ rule, canEdit }: { rule: MotivationRule; canEdit: boolean }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [tiers, setTiers] = useState<PayrollSnapshotTier[]>(rule.tiers);
  const [validFrom, setValidFrom] = useState(rule.valid_from);
  const [validTo, setValidTo] = useState<string | null>(rule.valid_to);
  const [comment, setComment] = useState(rule.comment ?? "");
  const mut = useMutation({
    mutationFn: () => patchRule(rule.id, {
      valid_from: validFrom, valid_to: validTo,
      tiers, advance_tiers: [], comment,
    }),
    onSuccess: () => { setEdit(false); qc.invalidateQueries({ queryKey: ["motivation-rules"] }); },
    onError: (e: Error) => alert((e as Error).message),
  });
  const delMut = useMutation({
    mutationFn: () => deleteRule(rule.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["motivation-rules"] }),
    onError: (e: Error) => alert((e as Error).message),
  });
  const actMut = useMutation({
    mutationFn: () => activateRule(rule.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["motivation-rules"] }),
    onError: (e: Error) => alert((e as Error).message),
  });
  const isActive = !rule.valid_to;
  return (
    <div style={{ border: "1px solid var(--line)", borderRadius: 10, padding: 14, background: "var(--paper-2)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 14 }}>Правило #{rule.id}</strong>
        <span style={{ fontSize: 12, color: "var(--mute)" }}>
          действует {fmtDate(rule.valid_from)} — {rule.valid_to ? fmtDate(rule.valid_to) : "сейчас"}
        </span>
        {isActive && <span className="badge" style={{ background: "rgba(61,122,61,0.18)", color: "var(--green)" }}>Активное</span>}
        {canEdit && !edit && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            {!isActive && (
              <button className="btn btn-sm" onClick={() => {
                if (!confirm(`Сделать «Правило #${rule.id}» активным? Текущее активное правило той же роли будет закрыто вчерашним числом.`)) return;
                actMut.mutate();
              }} disabled={actMut.isPending} title="Сделать активным (закроет другие активные правила этой роли)">
                {actMut.isPending ? "..." : <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="check" size={11}/>Сделать активным</span>}
              </button>
            )}
            <button className="btn btn-sm" onClick={() => setEdit(true)}>
              <Icon name="edit" size={11}/> Изменить
            </button>
            <button className="btn btn-sm" style={{ color: "var(--brick)" }}
                    onClick={() => {
                      if (!confirm(`Удалить «Правило #${rule.id}»? Действие необратимо.`)) return;
                      delMut.mutate();
                    }}
                    disabled={delMut.isPending}
                    title="Удалить правило">
              {delMut.isPending ? "..." : <><Icon name="trash" size={11}/> Удалить</>}
            </button>
          </div>
        )}
        {edit && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <button className="btn btn-sm" onClick={() => {
              setEdit(false);
              setTiers(rule.tiers);
              setValidFrom(rule.valid_from); setValidTo(rule.valid_to); setComment(rule.comment ?? "");
            }}>Отмена</button>
            <button className="btn btn-sm btn-primary" onClick={() => mut.mutate()} disabled={mut.isPending}>
              {mut.isPending ? "..." : "Сохранить"}
            </button>
          </div>
        )}
      </div>
      {rule.comment && !edit && <div style={{ fontSize: 12, color: "var(--mute)", marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}><Icon name="edit" size={11}/>{rule.comment}</div>}

      {edit && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 8, marginBottom: 10 }}>
          <label><div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4 }}>Дата начала</div>
            <input className="input-sm" type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)}/></label>
          <label><div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4 }}>Дата окончания</div>
            <input className="input-sm" type="date" value={validTo ?? ""} onChange={e => setValidTo(e.target.value || null)}/></label>
          <label><div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4 }}>Комментарий</div>
            <input className="input-sm" value={comment} onChange={e => setComment(e.target.value)}/></label>
        </div>
      )}

      {/* Тиры */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 6 }}>
          Тиры по % выполнения плана
        </div>
        <table className="tbl tbl-tight" style={{ fontSize: 13, width: "100%" }}>
          <thead><tr><th>От %</th><th>До %</th><th>Ставка %</th><th>Подпись</th>{edit && <th style={{ width: 30 }}/>}</tr></thead>
          <tbody>
            {tiers.map((t, i) => (
              <tr key={i}>
                {edit ? (<>
                  <td><input className="input-sm" type="number" value={t.min_pct} onChange={e => {
                    const next = [...tiers]; next[i] = { ...t, min_pct: Number(e.target.value) || 0 }; setTiers(next);
                  }}/></td>
                  <td><input className="input-sm" type="number" value={t.max_pct} onChange={e => {
                    const next = [...tiers]; next[i] = { ...t, max_pct: Number(e.target.value) || 0 }; setTiers(next);
                  }}/></td>
                  <td><input className="input-sm" type="number" step="0.0001" value={t.pct} onChange={e => {
                    const next = [...tiers]; next[i] = { ...t, pct: Number(e.target.value) || 0 }; setTiers(next);
                  }}/></td>
                  <td><input className="input-sm" value={t.label || ""} onChange={e => {
                    const next = [...tiers]; next[i] = { ...t, label: e.target.value }; setTiers(next);
                  }}/></td>
                  <td><button className="btn btn-sm" style={{ color: "var(--brick)" }} onClick={() => {
                    setTiers(tiers.filter((_, x) => x !== i));
                  }}>×</button></td>
                </>) : (<>
                  <td className="tabular">{t.min_pct}%</td>
                  <td className="tabular">{t.max_pct}%</td>
                  <td className="tabular strong">{(t.pct * 100).toFixed(3)}%</td>
                  <td style={{ color: "var(--mute)" }}>{t.label || "—"}</td>
                </>)}
              </tr>
            ))}
            {edit && (
              <tr>
                <td colSpan={5}>
                  <button className="btn btn-sm" onClick={() => setTiers([...tiers, { min_pct: 0, max_pct: 0, pct: 0, label: "" }])}>
                    + Добавить тир
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}

function NewRuleModal({ role, onClose, onCreated }: { role: string; onClose: () => void; onCreated: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [validFrom, setValidFrom] = useState(today);
  const [validTo, setValidTo] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [tiers] = useState<PayrollSnapshotTier[]>([
    { min_pct: 0, max_pct: 50, pct: 0.0, label: "<50%" },
    { min_pct: 50, max_pct: 100, pct: 0.0075, label: "50-100%" },
    { min_pct: 100, max_pct: 120, pct: 0.015, label: "100-120%" },
    { min_pct: 120, max_pct: 999, pct: 0.022, label: ">120%" },
  ]);
  const mut = useMutation({
    mutationFn: () => createRule({
      role, valid_from: validFrom, valid_to: validTo,
      tiers, advance_tiers: [], comment: comment.trim() || null,
    }),
    onSuccess: () => onCreated(),
    onError: (e: Error) => alert(e.message),
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-window" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{
            fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700,
            flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 12,
          }}>Новое правило мотивации</div>
          <button className="modal-close" onClick={onClose} title="Закрыть (Esc)" style={{ flexShrink: 0 }}>
            <Icon name="x" size={18}/>
          </button>
        </div>
        <div className="modal-body">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <label><div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4, letterSpacing: "0.04em", textTransform: "uppercase" }}>Действует с</div>
              <input className="input" type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)}/></label>
            <label><div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4, letterSpacing: "0.04em", textTransform: "uppercase" }}>Действует по</div>
              <input className="input" type="date" value={validTo ?? ""} onChange={e => setValidTo(e.target.value || null)}/></label>
          </div>
          <label><div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4, letterSpacing: "0.04em", textTransform: "uppercase" }}>Комментарий</div>
            <input className="input" value={comment} onChange={e => setComment(e.target.value)} placeholder="напр. «Новый регламент с июля»"/></label>
          <div style={{ fontSize: 12, color: "var(--mute)" }}>
            Тиры предзаполнены текущим регламентом — отредактируйте после создания.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 8, borderTop: "1px dotted var(--line)", marginTop: 4 }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={mut.isPending}>Отмена</button>
            <button className="btn btn-primary" onClick={() => mut.mutate()} disabled={mut.isPending}>
              {mut.isPending ? "..." : "Создать"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
